<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width: 900px;")
    div.d-flex.justify-center.align-center(v-if="isFetching" style="height: 80dvh;")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          yz-btn.ml-n3(icon-only icon="mdi-arrow-left" size="lg" color="transparent" @click="onGoBack")
        v-col.ml-3
          .yz-heading-5.text-neutral-700 {{ id ? 'Edit service' : 'Add service' }}
      v-form(ref="form")
        service-details-section(:product.sync="form.product" :type.sync="form.type" :cost.sync="form.cost" :groups.sync="groups" :hq-company-id="hqCompanyId" :service-id="id" :cost-rules="costRules" :currency-symbol="$currency.symbol")
        service-pricing-section(:pricing-tiers.sync="pricingTiers" :is-fetching-pricing-tiers="isFetchingPricingTiers" :price-overrides="priceOverrides" :branches="branches" :cost-rules="costRules" :currency-symbol="$currency.symbol" :currency-multiplier="$currency.multiplier" :currency-exponent="$currency.exponent" @open-tier-details="onOpenTierDetails" @go-to-price-tiers="onGoToPriceTiers" @open-override-dialog="showOverridePricingDialog = true" @edit-override="onEditOverride")
        service-branch-assignment-section(:assigned-branches.sync="assignedBranches" :branches="branches" :original-branch-company-ids="originalBranchCompanyIds" :booking-enabled-branch-ids="bookingEnabledBranchIds" :service-id="id" :company-slug="company.slug" :busy-branch-id="probingBranchId" @request-unassign="onRequestUnassign")

  v-footer.white(app inset padless elevation="3")
    v-container.px-8.py-2
      div.d-flex.align-center.justify-end.gap-2
        yz-btn(label="Cancel" size="lg" outlined @click="onGoBack")
        yz-btn(:label="id ? 'Save' : 'Finish'" size="lg" :loading="isLoading" @click="onSaveData")

  .dialogs
    pricing-tier-details-dialog(v-if="openPricingTierDetailsDialog" :open.sync="openPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")
    override-pricing-dialog(v-if="showOverridePricingDialog" :open.sync="showOverridePricingDialog" :branches="overrideBranchList" :price-tiers="pricingTiers" :uom-conversions="[]" :uom-conversion-enabled="false" :default-uom="null" :currency="$currency" :existing-override="editingOverride" @save="onSaveOverride" @closed="onCloseOverrideDialog")
    unassign-branch-warning-dialog(v-if="pendingUnassign" v-model="showUnassignWarning" :branch-id="pendingUnassign.branchId" :branch-name="pendingUnassign.branchName" :appointment-ids="pendingUnassign.appointmentIds" @request-bulk-cancel="onRequestBulkCancel" @view-appointments="onViewAppointments")
    unassign-branch-confirm-dialog(v-if="pendingUnassign" v-model="showUnassignConfirm" :branch-id="pendingUnassign.branchId" :branch-name="pendingUnassign.branchName" :service-id="id" :company-slug="company.slug" :appointment-ids="pendingUnassign.appointmentIds" @confirmed="onUnassignConfirmed" @cancel="onCancelUnassign")
    view-appointments-dialog(v-if="pendingUnassign" v-model="showViewAppointments" :branch-id="pendingUnassign.branchId" :branch-name="pendingUnassign.branchName" :appointments="pendingUnassign.appointments" @request-bulk-cancel="onRequestBulkCancel")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PricingTierDetailsDialog from '@/components/clinic/inventory/PricingTierDetailsDialog.vue'
import OverridePricingDialog from '@/components/hq/inventory/branches-warehouses/dialog/OverridePricingDialog.vue'
import ServiceDetailsSection from '@/components/hq/inventory/services/ServiceDetailsSection.vue'
import ServicePricingSection from '@/components/hq/inventory/services/ServicePricingSection.vue'
import ServiceBranchAssignmentSection from '@/components/hq/inventory/services/ServiceBranchAssignmentSection.vue'
import UnassignBranchWarningDialog from '@/components/hq/inventory/services/UnassignBranchWarningDialog.vue'
import UnassignBranchConfirmDialog from '@/components/hq/inventory/services/UnassignBranchConfirmDialog.vue'
import ViewAppointmentsDialog from '@/components/hq/inventory/services/ViewAppointmentsDialog.vue'

export default {
  components: { PricingTierDetailsDialog, OverridePricingDialog, ServiceDetailsSection, ServicePricingSection, ServiceBranchAssignmentSection, UnassignBranchWarningDialog, UnassignBranchConfirmDialog, ViewAppointmentsDialog },
  beforeRouteEnter (to, from, next) {
    if (from.name) {
      to.meta.hasPrevPath = true
    }
    next()
  },
  props: ['id'],
  data () {
    return {
      costRules: [v => (!v || Number(v) >= 0) || 'Cost must be positive'],
      isLoading: false,
      isFetching: false,
      isFetchingPricingTiers: false,
      openPricingTierDetailsDialog: false,
      selectedTier: null,
      form: {
        product: { name: '', description: '' },
        type: null,
        cost: null,
      },
      groups: [],
      pricingTiers: [],
      totalPanelCount: 0,
      priceOverrides: [],
      showOverridePricingDialog: false,
      editingOverrideIndex: null,
      editingOverride: null,
      assignedBranches: [],
      branches: [],
      originalBranchCompanyIds: [],
      bookingEnabledBranchIds: [],
      branchServiceIdByCompanyId: {},
      probingBranchId: null,
      pendingUnassign: null,
      showUnassignWarning: false,
      showUnassignConfirm: false,
      showViewAppointments: false,
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    hqCompanyId () {
      return this.company?.branch_of?.id || this.company?.id
    },
    overrideBranchList () {
      const usedBranches = new Set(this.priceOverrides.flatMap(o => o.branches))
      if (this.editingOverride) {
        this.editingOverride.branches.forEach(b => usedBranches.delete(b))
      }
      return this.branches
        .filter(b => !usedBranches.has(b.id))
        .map(b => ({ companyId: b.id, name: b.name, enabled: true }))
    },
  },
  mounted () {
    if (this.id) {
      this.fetchService()
    } else {
      this.fetchPricingTiers()
      this.fetchBranches()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchService () {
      this.isFetching = true
      try {
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/hq/services/${this.id}`)
        const data = resp.data
        this.form = {
          product: { name: data.product.name, description: data.product.description || '' },
          type: data.type,
          cost: data.cost / this.$currency.multiplier,
        }
        this.groups = data.groups || []
        await this.fetchPricingTiers(data.hq_price_tiers || [])
        await this.fetchBranches()
        if (data.branches && data.branches.length > 0) {
          for (const branch of data.branches) {
            if (branch.company) {
              this.branchServiceIdByCompanyId[branch.company.id] = branch.service_id
            }
            if (branch.price_tiers && branch.price_tiers.length > 0) {
              this.priceOverrides.push({
                branches: [branch.company.id],
                priceTiers: branch.price_tiers.map(pt => ({
                  price_tier: pt.price_tier?.id || pt.price_tier,
                  price: pt.price ? (pt.price / this.$currency.multiplier).toFixed(this.$currency.exponent) : null,
                  name: pt.price_tier?.name || '',
                  description: pt.price_tier?.description || '',
                }))
              })
            }
            if (branch.company && !branch.deleted) {
              this.originalBranchCompanyIds.push(branch.company.id)
              this.assignedBranches.push({ company: branch.company.id, deleted: false })
              if (branch.booking_enabled) {
                this.bookingEnabledBranchIds.push(branch.company.id)
              }
            }
          }
        }
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    async fetchPricingTiers (savedTiers) {
      this.isFetchingPricingTiers = true
      try {
        const params = { company: this.hqCompanyId, status: 'active', page_size: -1 }
        const resp = await this.$axios.get('product_pricetiers', { params })
        const results = resp.data?.results || resp.data || []
        this.totalPanelCount = resp.data?.total_panels || 0
        const multiplier = Number(this.$currency.multiplier)
        const exponent = this.$currency.exponent
        this.pricingTiers = results.map(tier => {
          const match = savedTiers ? savedTiers.find(pt => (pt.price_tier?.id || pt.price_tier) === tier.id) : null
          return {
            id: tier.id,
            name: tier.name,
            description: tier.description,
            panels: tier.panels || [],
            self_pay: tier.self_pay || [],
            panelText: this.getPanelCount(tier.panels),
            selfPayText: this.getSelfPayCount(tier.self_pay),
            price: match && match.price ? (match.price / multiplier).toFixed(exponent) : null,
            expanded: false
          }
        })
      } catch (e) {
        console.error(e)
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetchingPricingTiers = false
      }
    },
    async fetchBranches () {
      try {
        const resp = await this.$axios.get(`/companies/${this.hqCompanyId}/branches`, { params: { os: 'clinic', page_size: -1 } })
        if (resp.data && resp.data.results) {
          this.branches = resp.data.results
        }
      } catch (e) {
        console.error(e)
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    buildPayload () {
      const data = {
        product: { name: this.form.product.name, description: this.form.product.description },
        type: this.form.type,
        cost: Math.round(this.form.cost * this.$currency.multiplier),
        groups: this.groups.map(m => m.id),
      }
      data.hq_price_tiers = this.pricingTiers
        .filter(f => f.price !== null && f.price !== '')
        .map(m => ({ price_tier: m.id, price: Math.round(Number(m.price) * Number(this.$currency.multiplier)) }))
      data.branch_price_tiers = data.hq_price_tiers
      if (this.branches.length > 0) {
        const activeIds = new Set(this.assignedBranches.filter(b => !b.deleted).map(b => b.company))
        data.branches = this.branches.map(branch => {
          const isActive = activeIds.has(branch.id)
          if (isActive) {
            const branchData = { company: branch.id, deleted: false }
            const override = this.priceOverrides.find(o => o.branches.includes(branch.id))
            if (override) {
              branchData.price_tiers = override.priceTiers
                .filter(f => f.price != null && f.price !== '')
                .map(m => ({ price_tier: m.price_tier, price: Math.round(Number(m.price) * Number(this.$currency.multiplier)) }))
            }
            return branchData
          }
          return { company: branch.id, deleted: true }
        })
      }
      return data
    },
    async onSaveData () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      this.isLoading = true
      const payload = this.buildPayload()
      try {
        if (this.id) {
          await this.$axios.patch(`/clinics/${this.company.slug}/hq/services/${this.id}`, payload)
          this.showMessage(['success', this.$t('success_message.update_successful')])
        } else {
          await this.$axios.post(`/clinics/${this.company.slug}/hq/services`, payload)
          this.showMessage(['success', 'Service created successfully'])
        }
        this.onGoBack()
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    async onRequestUnassign ({ branchId, branchName }) {
      if (this.probingBranchId) return
      this.probingBranchId = branchId
      try {
        const branchServiceId = this.branchServiceIdByCompanyId[branchId]
        if (!branchServiceId) {
          this.pendingUnassign = { branchId, branchName, branchServiceId: null, appointmentIds: [] }
          this.showUnassignConfirm = true
          return
        }
        const params = {
          companies: branchId,
          services: branchServiceId,
          exclude_statuses: 'received,reject,absent,cancelled,deleted',
          page_size: -1,
        }
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/hq/appointments`, { params })
        const appointments = resp.data?.results || resp.data || []
        this.pendingUnassign = {
          branchId,
          branchName,
          branchServiceId,
          appointments,
          appointmentIds: appointments.map(a => a.id),
        }
        if (appointments.length > 0) {
          this.showUnassignWarning = true
        } else {
          this.showUnassignConfirm = true
        }
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.probingBranchId = null
      }
    },
    onRequestBulkCancel ({ branchId, branchName, appointmentIds }) {
      this.pendingUnassign = { branchId, branchName, appointmentIds }
      this.showUnassignConfirm = true
    },
    onViewAppointments () {
      this.showViewAppointments = true
    },
    onCancelUnassign () {
      this.showUnassignConfirm = false
      this.showUnassignWarning = false
      this.showViewAppointments = false
      this.pendingUnassign = null
    },
    async onUnassignConfirmed ({ branchId }) {
      const branches = this.branches.map(b => {
        if (b.id === branchId) return { company: b.id, deleted: true }
        const isActive = this.assignedBranches.some(ab => ab.company === b.id && !ab.deleted)
        return { company: b.id, deleted: !isActive }
      })
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/hq/services/${this.id}`, { branches })
        const next = [...this.assignedBranches]
        const idx = next.findIndex(b => b.company === branchId)
        if (idx >= 0) next[idx] = { ...next[idx], deleted: true }
        else next.push({ company: branchId, deleted: true })
        this.assignedBranches = next
        this.originalBranchCompanyIds = this.originalBranchCompanyIds.filter(id => id !== branchId)
        this.showMessage(['success', this.$t('success_message.update_successful')])
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.pendingUnassign = null
      }
    },
    getPanelCount (panels) {
      const count = panels ? panels.length : 0
      if (count === 0) return 'None included'
      if (this.totalPanelCount > 0 && count === this.totalPanelCount) return 'All included'
      return count + ' included'
    },
    getSelfPayCount (methods) {
      if (!methods) return 'None included'
      return methods.length === 0 ? 'None included' : methods.length + ' included'
    },
    onOpenTierDetails (item) {
      this.selectedTier = item
      this.openPricingTierDetailsDialog = true
    },
    onEditOverride (index) {
      this.editingOverrideIndex = index
      this.editingOverride = JSON.parse(JSON.stringify(this.priceOverrides[index]))
      this.showOverridePricingDialog = true
    },
    onSaveOverride (overrideData) {
      const savedBranches = new Set(overrideData.branches)
      for (let i = this.priceOverrides.length - 1; i >= 0; i--) {
        if (i === this.editingOverrideIndex) continue
        this.priceOverrides[i].branches = this.priceOverrides[i].branches.filter(b => !savedBranches.has(b))
        if (this.priceOverrides[i].branches.length === 0) {
          this.priceOverrides.splice(i, 1)
          if (this.editingOverrideIndex !== null && i < this.editingOverrideIndex) {
            this.editingOverrideIndex--
          }
        }
      }
      overrideData.priceTiers = overrideData.priceTiers.map(t => {
        const tier = this.pricingTiers.find(pt => pt.id === t.price_tier)
        return { ...t, name: tier?.name || '', description: tier?.description || '' }
      })
      if (this.editingOverrideIndex !== null) {
        this.$set(this.priceOverrides, this.editingOverrideIndex, overrideData)
      } else {
        this.priceOverrides.push(overrideData)
      }
      this.onCloseOverrideDialog()
    },
    onCloseOverrideDialog () {
      this.showOverridePricingDialog = false
      this.editingOverrideIndex = null
      this.editingOverride = null
    },
    onGoToPriceTiers () {
      this.$router.push('/admin/hq/inventory/settings/price-tier')
    },
    onGoBack () {
      if (this.$route?.meta?.hasPrevPath) {
        this.$router.back()
        return
      }
      this.$router.push('/admin/hq/inventory/services')
    },
  }
}
</script>
